import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { TriangleAlert } from "lucide-react";

/**
 * Oct 2026 — capacity-reduction guardrail confirmation. Surfaced by both
 * the recurring-rule capacity field and (if ever wired up) a direct
 * per-slot capacity edit, which share the same 409 `requires_confirmation`
 * shape from the backend (existing_bookings / current_capacity /
 * requested_capacity / earliest_date / latest_date).
 */
export function CapacityGuardrailDialog({ warning, onCancel, onConfirm, busy }) {
  if (!warning) return null;
  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent data-testid="capacity-guardrail-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
            <TriangleAlert className="w-5 h-5" /> {warning.warning || "Existing bookings exceed the new capacity"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-slate-600 space-y-3">
              <p>{warning.message}</p>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5 text-xs text-slate-700">
                {warning.technician_name && <p className="font-semibold">Technician: {warning.technician_name}</p>}
                {warning.weekday_name && <p>Weekday: {warning.weekday_name}</p>}
                {warning.date && <p>Date: {warning.date}</p>}
                <p data-testid="capacity-guardrail-bookings">Existing confirmed bookings: {warning.existing_bookings}</p>
                <p>Current capacity: {warning.current_capacity} → Requested: {warning.requested_capacity}</p>
                {warning.affected_slots > 1 && <p>{warning.affected_slots} future dates affected</p>}
                {warning.earliest_date && warning.latest_date && (
                  <p>Affected dates: {warning.earliest_date} → {warning.latest_date}</p>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="capacity-guardrail-cancel" onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="capacity-guardrail-confirm"
            disabled={busy}
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700">
            {warning.confirm_action || "Save and preserve existing bookings"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
